import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { toDisplayTime } from "../../../../utilis/date-formats";
import { CUSTOM_TAB_TYPE, DASHBOARD_FILTER_TYPE } from "../../../../types/common.type";
import { getMedicareExhaustionDate } from "../../../../utilis/overall-common";
import { OVERALL_CARDS_TYPE } from "../../../../types/overall.type";
import _ from "lodash";
import { Box, CircularProgress, IconButton, Stack, TableSortLabel, Typography, useMediaQuery } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import ResidentDetails from "../../../landing/facility/data/residentDetails";
import { expandedColumnsDefault } from "../../../../types/pages.type";
import HtmlTooltip from "../../HtmlTooltip";
import EditSVG from "../../../../assets/svgs/edit.svg";
import AddPatient from "../../../landing/facility/data/add-patient/AddPatient";
import { getCensusFirstDate } from "../../../../services/data-input.service";
import { useSelector } from "react-redux";
import ActivityLogTooltip from "../../ActivityLogTooltip";
import InfoIcon from "../../../icon/InfoIcon";
import CustomTooltipContentWithAction, { richTooltipSlotProps } from "../../CustomTooltipContentWithAction";
import { patientDataOrderBy } from "../../../../utilis/common";
import { QUESTION_INPUT_TYPE } from "../../../../types/question.type";
import { useDashboard } from "../../../../context/DashboardContext";
import { createSetting, getColumnOrders } from "../../../../services/settings.service";
import useFacilityId from "../../../../hooks/useFacilityId";
import {
    resetChartTableSearchNav,
    updateChartTableSearchNav,
} from "../chartTableSearchNavBridge";

// Create a sortable table cell component
function SortableTableCell({ id, children, ...props }) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.8 : 1,
        zIndex: isDragging ? 1000 : 1,
        cursor: isDragging ? 'grabbing' : 'grab',
        backgroundColor: isDragging ? '#f5f5f5' : 'inherit',
        boxShadow: isDragging ? '0 2px 8px rgba(0,0,0,0.1)' : 'none',
    };

    return (
        <TableCell
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            {...props}
        >
            {children}
        </TableCell>
    );
}

export default function ChartTableList({
    data,
    getFieldNames,
    relation,
    pageType = null,
    tableElementRef,
    questions = [],
    searchTerm,
    handleHighlightedCount,
    filterType,
    selectedHighlightedColumns,
    orderData,
    active,
    activeFacilities,
    filters,
    isNotCombineTab = true,
    tableFilterType = null,
    isAdt = false
}) {
    const isMobile = useMediaQuery('(max-width: 768px)');
    const localTableContainerRef = React.useRef(null);
    const touchStartRef = React.useRef({ x: 0, y: 0 });
    const scrollDirectionRef = React.useRef(null);
    const [isScrollingVertically, setIsScrollingVertically] = React.useState(false);
    const [isScrollingHorizontally, setIsScrollingHorizontally] = React.useState(false);

    const handleTouchStart = (e) => {
        if (!isMobile) return;
        const touch = e.touches[0];
        touchStartRef.current = { x: touch.clientX, y: touch.clientY };
        scrollDirectionRef.current = null;
    };

    const handleTouchMove = (e) => {
        if (!isMobile) return;
        const touch = e.touches[0];
        const deltaX = Math.abs(touch.clientX - touchStartRef.current.x);
        const deltaY = Math.abs(touch.clientY - touchStartRef.current.y);

        if (!scrollDirectionRef.current && (deltaX > 5 || deltaY > 5)) {
            if (deltaY > deltaX) {
                scrollDirectionRef.current = 'vertical';
                setIsScrollingVertically(true);
            } else {
                scrollDirectionRef.current = 'horizontal';
                setIsScrollingHorizontally(true);
            }
        }
    };

    const handleTouchEnd = () => {
        scrollDirectionRef.current = null;
        setIsScrollingVertically(false);
        setIsScrollingHorizontally(false);
    };

    const { orderBy, setOrderBy, order, setOrder } = orderData
    let [highlightedCountDefaultValue, setHighlightedCountDefaultValue] = React.useState(0);
    const countedRowsRef = React.useRef(new Set());
    const { dashboardData: { isOnlyHospitalDashboard = false } } = useDashboard();
    const [columnOrder, setColumnOrder] = React.useState([]);
    const [originalQuestions, setOriginalQuestions] = React.useState([]); // eslint-disable-line
    const [isLoadingColumnOrder, setIsLoadingColumnOrder] = React.useState(true);
    const { facilityId: facilityIdFromHook } = useFacilityId();
    // Setup sensors for drag and drop
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    React.useEffect(() => {
        setHighlightedCountDefaultValue(0);
        countedRowsRef.current = new Set();
        // Also reset the count when search term is cleared
        if (!searchTerm) {
            handleHighlightedCount(0);
        }
    }, [filterType?.operation, searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

    const [selectedResident, setSelectedResident] = React.useState(undefined);
    const [chartData, setChartData] = React.useState(data);
    const { permission } = useSelector(({ permission }) => ({ permission }));
    const isWriteAccess = React.useMemo(
        () => permission?.facilityAccess?.write ?? false,
        [permission]
    );

    const [previousColor, setPreviousColor] = React.useState(null);

    React.useEffect(() => {
        setChartData(data);
    }, [data]);

    // Load saved column order for user
    const loadColumnOrder = React.useCallback(async () => {
        if (questions && questions.length > 0) {
            setIsLoadingColumnOrder(true);
            try {
                // const pageTypeForColumnOrder = isAdt ? filters.selectedCardItem.parentId : filters?.question?.isAnotherDashboard ? filters?.question?.dashboardType : pageType;
                // const selectedTabForColumnOrder = ((filters?.question?.dashboardType === DASHBOARD_FILTER_TYPE.COMMUNITY_TRANSFER && tableFilterType) || pageType === DASHBOARD_FILTER_TYPE.COMMUNITY_TRANSFER || (pageType === DASHBOARD_FILTER_TYPE.OVERALL && tableFilterType) || isAdt) ? tableFilterType : "all";
                let selectedTabForColumnOrder = pageType;
                if (isAdt) {
                    selectedTabForColumnOrder = filters?.selectedCardItem?.parentId;
                }
                if (filters?.question?.isAnotherDashboard) {
                    selectedTabForColumnOrder = filters?.question?.dashboardType;
                }
                // isAdt ? filters?.selectedCardItem?.parentId : filters?.question?.isAnotherDashboard ? filters?.question?.dashboardType : pageType;
                if (tableFilterType && tableFilterType !== "all") {
                    selectedTabForColumnOrder = tableFilterType === "hospitalTransfer" ? "hospital" : tableFilterType;
                }
                const savedOrder = await getColumnOrders({
                    type: "columnOrder",
                    pageType: selectedTabForColumnOrder,
                    //       selectedTab: selectedTabForColumnOrder,
                });

                if (savedOrder && savedOrder.length > 0 && savedOrder[0]?.data?.columnOrder) {
                    const savedColumnOrder = savedOrder[0].data.columnOrder;

                    // Create ordered questions based on saved order
                    const orderedQuestions = [];
                    const remainingQuestions = [...questions];

                    // First, add questions in saved order
                    savedColumnOrder.forEach(accessor => {
                        const questionIndex = remainingQuestions.findIndex(q => q?.question?.accessor === accessor);
                        if (questionIndex !== -1) {
                            orderedQuestions.push(remainingQuestions[questionIndex]);
                            remainingQuestions.splice(questionIndex, 1);
                        }
                    });

                    // Add any remaining questions that weren't in saved order
                    orderedQuestions.push(...remainingQuestions);

                    setColumnOrder(orderedQuestions);
                } else {
                    setColumnOrder([...questions]);
                }
            } catch (error) {
                setColumnOrder([...questions]);
            } finally {
                setIsLoadingColumnOrder(false);
            }
        } else {
            setColumnOrder([]);
            setIsLoadingColumnOrder(false);
        }

    }, [questions, isAdt, filters?.selectedCardItem?.parentId, filters?.question?.isAnotherDashboard, filters?.question?.dashboardType, pageType, tableFilterType]);

    const getFinalColumnOrder = React.useCallback(() => {
        const currentTabAccessor = filters?.question?.accessor || filters?.accessor;
        let reorderableQuestions = columnOrder.filter(q => q?.question?.accessor !== 'dateOfADT');

        // If we're on a specific tab, prioritize that column for display only
        if (currentTabAccessor) {
            const currentTabQuestionIndex = reorderableQuestions.findIndex(
                q => q?.question?.accessor === currentTabAccessor
            );

            if (currentTabQuestionIndex !== -1) {
                const currentTabQuestion = reorderableQuestions[currentTabQuestionIndex];
                reorderableQuestions.splice(currentTabQuestionIndex, 1);
                reorderableQuestions.unshift(currentTabQuestion);
            }
        }

        return reorderableQuestions;
    }, [columnOrder, filters?.question?.accessor, filters?.accessor]);

    // Update getReorderableColumns to use the final order
    const getReorderableColumns = () => {
        const finalOrder = getFinalColumnOrder();

        return finalOrder.map(ele => ({
            accessor: ele?.question?.accessor,
            label: ele?.order?.tableLabel || ele?.order?.label || ele?.question?.tableLabel || ele?.question?.label,
            question: ele?.question,
            isFixed: false
        }));
    };

    // Save column order to backend
    const saveColumnOrder = React.useCallback(async (newOrder) => {
        if (!pageType) return;

        try {
            const accessors = newOrder.map(q => q?.question?.accessor).filter(Boolean);
            //const pageTypeForColumnOrder = isAdt ? filters.selectedCardItem.parentId : filters?.question?.isAnotherDashboard ? filters?.question?.dashboardType : pageType;
            // let selectedTabForColumnOrder = 
            let selectedTabForColumnOrder = pageType;
            if (isAdt) {
                selectedTabForColumnOrder = filters?.selectedCardItem?.parentId;
            }
            if (filters?.question?.isAnotherDashboard) {
                selectedTabForColumnOrder = filters?.question?.dashboardType;
            }
            // isAdt ? filters?.selectedCardItem?.parentId : filters?.question?.isAnotherDashboard ? filters?.question?.dashboardType : pageType;
            if (tableFilterType && tableFilterType !== "all") {
                selectedTabForColumnOrder = tableFilterType === "hospitalTransfer" ? "hospital" : tableFilterType;
            }
            // ((filters?.question?.dashboardType === DASHBOARD_FILTER_TYPE.COMMUNITY_TRANSFER && tableFilterType) || pageType === DASHBOARD_FILTER_TYPE.COMMUNITY_TRANSFER || (pageType === DASHBOARD_FILTER_TYPE.OVERALL && tableFilterType) || isAdt) ? tableFilterType : "all";
            await createSetting({
                type: "columnOrder",
                pageType: selectedTabForColumnOrder,
                // selectedTab: selectedTabForColumnOrder,
                data: { columnOrder: accessors }
            });
        } catch (error) {
            console.error('Error saving column order:', error);
        }
    }, [pageType, isAdt, filters?.selectedCardItem?.parentId, filters?.question?.isAnotherDashboard, filters?.question?.dashboardType, tableFilterType]);

    // Simplify the handleDragEnd function
    const handleDragEnd = React.useCallback((event) => {
        const { active, over } = event;

        if (!over || active.id === over.id) {
            return;
        }
        // Work with the base column order (without tab prioritization)
        const baseOrder = columnOrder.filter(q => q?.question?.accessor !== 'dateOfADT');

        // Find the indices in the base order
        const oldIndex = baseOrder.findIndex(q => q?.question?.accessor === active.id);
        const newIndex = baseOrder.findIndex(q => q?.question?.accessor === over.id);

        if (oldIndex === -1 || newIndex === -1) return;

        // Perform the reordering on base order
        const newBaseOrder = arrayMove(baseOrder, oldIndex, newIndex);
        // Update the main columnOrder state
        setColumnOrder(newBaseOrder);

        // Save to backend
        saveColumnOrder(newBaseOrder);
    }, [columnOrder, saveColumnOrder]);

    const highlightText = (text, accessor, setCount, toolTipContent, rowIndex) => {
        if (!searchTerm) return text;
        let isHighlighted = true;
        if (selectedHighlightedColumns && selectedHighlightedColumns.length > 0) {
            isHighlighted =
                accessor &&
                Array.isArray(selectedHighlightedColumns) &&
                (selectedHighlightedColumns.includes("selectAll") ||
                    selectedHighlightedColumns.includes(accessor));
        }
        const regex = new RegExp(`(${searchTerm})`, filterType.type === 'number' && filterType.operation ? 'g' : 'gi');
        if (text !== null && isHighlighted) {
            let parts;
            if (filterType.type === 'number' && filterType.operation) {
                let isNumberToolTip;
                let textValueToolTip;
                let isMatchToolTip;
                if (toolTipContent) {
                    isNumberToolTip = !isNaN(toolTipContent) && toolTipContent !== null;
                    textValueToolTip = isNumberToolTip ? Number(toolTipContent) : toolTipContent;
                }
                const isNumber = !isNaN(text) && text !== null;
                const textValue = isNumber ? Number(text) : text;
                if (isNumber) {
                    isMatchToolTip = filterType.operation === 'lessThan' ? textValueToolTip <= searchTerm : textValueToolTip >= searchTerm;
                    const isMatch = filterType.operation === 'lessThan' ? textValue <= searchTerm : textValue >= searchTerm;
                    if (isMatch) {
                        if (isMatchToolTip) {
                            parts = <span style={{ backgroundColor: 'yellow' }}>{text?.substring(0, 3)}</span>;
                        } else {
                            parts = <span style={{ backgroundColor: 'yellow' }}>{text}</span>;
                        }
                        // Count only once per row for number operations, but only if column is highlighted
                        if (isHighlighted && rowIndex !== undefined && !countedRowsRef.current.has(rowIndex)) {
                            countedRowsRef.current.add(rowIndex);
                            highlightedCountDefaultValue++;
                            setCount(highlightedCountDefaultValue);
                        }
                    } else {
                        parts = text;
                    }
                } else {
                    parts = text;
                }
                return parts;
            } else {
                let partsTooltip;
                let isMatchToolTip = 0;
                if (toolTipContent) {
                    partsTooltip = String(toolTipContent).split(regex);
                    partsTooltip?.forEach(part => {
                        if (regex.test(part)) {
                            isMatchToolTip++;
                        }
                    });
                }
                parts = String(text ?? '').split(regex);
                // Count only once per row, even if there are multiple matches, but only if column is highlighted
                const hasMatch = parts.some(part => regex.test(part));
                if (hasMatch && isHighlighted && rowIndex !== undefined && !countedRowsRef.current.has(rowIndex)) {
                    countedRowsRef.current.add(rowIndex);
                    highlightedCountDefaultValue++;
                    setCount(highlightedCountDefaultValue);
                }
                parts = parts.map((part, index) => {
                    if (isMatchToolTip > 0 && part.includes('...')) {
                        const parts = part.split('...');
                        return (
                            <span key={index}>
                                {parts.map((subPart, subIndex) => (
                                    <React.Fragment key={subIndex}>
                                        {subPart}
                                        {subIndex < parts.length - 1 && (
                                            <span style={{ backgroundColor: 'yellow' }}>...</span>
                                        )}
                                    </React.Fragment>
                                ))}
                            </span>
                        );
                    }
                    return regex.test(part) ? <span key={index} style={{ backgroundColor: 'yellow' }}>{part}</span> : part
                });
                return parts;
            }
        } else {
            return text;
        }
    };


    const getTooltipText = (text, accessor = null, isHighlighted = true, rowIndex) => {
        const count = 25, variant = "body2", isLightTooltip = false;
        if (!text) return null;
        const tooltipContent = text.length > count ? `${text?.substring(0, count)}...` : text;
        const tooltipTitle = (
            <CustomTooltipContentWithAction
                text={text?.length > count ? highlightText(text, accessor, handleHighlightedCount, null, rowIndex) : text}
            />
        );
        const tooltipProps = {
            title: tooltipTitle,
            arrow: true,
            componentsProps: richTooltipSlotProps,
        };

        return (
            <Typography
                variant={variant}
                noWrap
                sx={{
                    display: "block",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: "100%",
                    ...!isHighlighted && {
                        textTransform: "uppercase",
                        fontFamily: '"manrope-bold"',
                        fontSize: isMobile ? "8px" : "12px",
                        lineHeight: 1.4,
                        color: "#000",
                    }
                }}
            >
                {text.length > count ? (
                    isLightTooltip ? (
                        <HtmlTooltip PopperProps={{ style: { zIndex: 1000 } }} {...tooltipProps} key={text + "tooltip_html"} disableFocusListener>
                            <span style={{ cursor: "pointer" }}>
                                {isHighlighted ? highlightText(tooltipContent, accessor, handleHighlightedCount, null, rowIndex) : tooltipContent}
                            </span>
                        </HtmlTooltip>
                    ) : (
                        <Tooltip
                            {...tooltipProps}
                            PopperProps={{
                                stopPropagation: true,
                                onClick: (e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                },
                            }}
                            key={text + "tooltip"}
                            disableFocusListener
                        >
                            <span style={{ cursor: "pointer" }}>
                                {isHighlighted ? highlightText(tooltipContent, accessor, handleHighlightedCount, text?.substring(count), rowIndex) : tooltipContent}
                            </span>
                        </Tooltip>
                    )
                ) : (
                    <span style={{ cursor: "pointer" }}>{isHighlighted ? highlightText(text || "", accessor, handleHighlightedCount, null, rowIndex) : text}</span>
                )}
            </Typography>
        );
    };

    const getQuestionAnswer = (question, row, rowIndex) => {
        const { accessor, type, validationType, multiple, customQuestionInputType, invisibleAnswerOptions, dataInputLabel } = question;
        if (
            customQuestionInputType === QUESTION_INPUT_TYPE.UNLIMITED_ANSWERS &&
            question?.validationOptions?.length > 0
        ) {
            const foundOption = question.validationOptions.find(
                (option) => option._id === row[accessor]
            );
            if (foundOption) {
                return foundOption.label;
            }
        }

        if (invisibleAnswerOptions && dataInputLabel) {
            const invisibleLabels = dataInputLabel.split(',').map(label => label.trim()).filter(Boolean);
            if (invisibleLabels.includes(row[accessor])) {
                return highlightText(row[accessor], accessor, handleHighlightedCount, null, rowIndex);
            }
        }

        switch (type) {
            case "text":
                return row[accessor] ? getTooltipText(row[accessor], accessor, true, rowIndex) : null;
            case "toggle":
                if (accessor === "transferType") {
                    return highlightText(row[accessor], accessor, handleHighlightedCount, null, rowIndex);
                } else {
                    return highlightText(row[accessor] ? "Yes" : "No", accessor, handleHighlightedCount, null, rowIndex);
                }
            case "date":
                if (accessor === "dateOfLatestAdmission") {
                    const value = row[accessor] ? row[accessor] : row?.dateOfActualAdmission;
                    if (value) {
                        return highlightText(toDisplayTime(value, "MM/DD/YYYY"), accessor, handleHighlightedCount, null, rowIndex);
                    } else {
                        return "-";
                    }
                }
                return row[accessor] ? highlightText(toDisplayTime(row[accessor], "MM/DD/YYYY"), accessor, handleHighlightedCount, null, rowIndex) : "-";
            case "time":
                return row[accessor] ? highlightText(row[accessor], accessor, handleHighlightedCount, null, rowIndex) : null;
            case "validation":
                if (multiple) {
                    const dxAnswer = row[validationType]
                        .map((prodData) => prodData.label)
                        .join(", ");
                    return dxAnswer ? getTooltipText(dxAnswer, accessor, true, rowIndex) : "N/A";
                } else {
                    const labelAccessor = accessor === "payerSourceInsurance" && validationType === "insurance" ? "payerSourceInsurance" : validationType;
                    return row[labelAccessor]
                        ? getTooltipText(row[labelAccessor]?.label, accessor, true, rowIndex)
                        : "N/A";
                }
            default:
                return row[accessor] ? highlightText(row[accessor], accessor, handleHighlightedCount, null, rowIndex) : null;
        }
    };

    function getTableColumnsForDetails() {
        const dateOfADTColumn = {
            Header: "Date of ADT",
            accessor: "dateOfADT",
            Cell: ({ row }) => <div>{toDisplayTime(row.original.dateOfADT)}</div>,
        };
        const hasDateOfADTAccessor = questions.some(
            (q) => q?.question?.accessor === "dateOfADT"
        );
        const dynamicColumns = questions.map(question => ({
            Header: question.order?.tableLabel || question.order?.label || question.question?.tableLabel || question.question?.label,
            accessor: question.question?.accessor,
            Cell: ({ row }) => row ? getQuestionAnswer(question.question, row.original) || "N/A" : "N/A",
        }));
        const baseColumns = hasDateOfADTAccessor
            ? expandedColumnsDefault
            : [...expandedColumnsDefault, dateOfADTColumn];
        return [...baseColumns, ...dynamicColumns];
    }

    const [showAdd, setShowAdd] = React.useState(false);
    const [editResident, setEditResident] = React.useState(undefined);
    const toggleAdd = () => setShowAdd(!showAdd);
    const [initialDate, setInitialDate] = React.useState(null);
    const getInitialCensusDate = async () => {
        const facilityId = facilityIdFromHook;
        await getCensusFirstDate(facilityId).then((res) => {
            if (res) {
                setInitialDate(res?.date);
            }
        });
    };

    React.useEffect(() => {
        getInitialCensusDate();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [facilityIdFromHook]);

    const patientAdded = (patient) => {
        if (patient) {
            setChartData((prev) =>
                prev.map((userScore) => {
                    if (userScore._id === patient?._id) {
                        return {
                            ...userScore,
                            ...patient,
                        };
                    } else {
                        return userScore;
                    }
                })
            );
        }
    };

    const getHighLightColor = React.useCallback((fieldName, row) => {
        if (row && row?.highlighter && row?.highlighter.length > 0) {
            const color = row?.highlighter.find(
                (ele) => ele?.fieldName === fieldName
            )?.color;
            return color;
        } else {
            return null;
        }
    }, []);

    const handleScrollTo = React.useCallback((fieldName, color) => {
        if (previousColor && fieldName === previousColor?.fieldName) {
            const elementPrevious = document.getElementById(previousColor?.fieldName);
            elementPrevious.style.border = "";
            elementPrevious.style.borderBottom = "1px solid " + previousColor?.color;
            setPreviousColor(null);
        } else {
            const element = document.getElementById(fieldName);

            if (previousColor) {
                const elementPrevious = document.getElementById(previousColor?.fieldName);
                elementPrevious.style.border = "";
                elementPrevious.style.borderBottom = "1px solid " + previousColor?.color;
            }
            if (element) {
                element.style.border = "1px solid " + color;

                const parentContainer = element.closest(".print-table-scrollable");
                const table = element.closest(".scrollable-container").querySelector("table");
                let fixedColumnsWidth = 0;

                if (table) {
                    const firstRow = table.querySelector("thead tr");
                    if (firstRow) {
                        const firstThreeTHs = firstRow.querySelectorAll("th:nth-child(-n+3)");
                        fixedColumnsWidth = Array.from(firstThreeTHs).reduce((acc, th) => acc + th.offsetWidth, 0);
                    }
                }

                const scrollContainer = element.closest(".scrollable-container");
                if (scrollContainer) {
                    const containerRect = scrollContainer.getBoundingClientRect();
                    const cellRect = element.getBoundingClientRect();
                    const targetScrollLeft =
                        scrollContainer.scrollLeft +
                        (cellRect.left - containerRect.left) -
                        fixedColumnsWidth;
                    scrollContainer.scrollLeft = Math.max(targetScrollLeft, 0);
                } else if (parentContainer) {
                    const leftOffset = parentContainer.offsetLeft;
                    parentContainer.scrollLeft = Math.max(element.offsetLeft - leftOffset - fixedColumnsWidth, 0);
                }
                setPreviousColor({ fieldName, color });
            }
        }
    }, [previousColor]);

    // Handle sorting logic
    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const capitalizeFirstLetter = (value) => {
        if (value == null) return "";
        const str = String(value).trim();
        if (!str || str === "-") return str;
        return str.charAt(0).toUpperCase() + str.slice(1);
    };

    const getTypeDisplayRaw = React.useCallback(
        (row) => {
            if (!isNotCombineTab) {
                if (
                    filters?.question?.customTab?.type === CUSTOM_TAB_TYPE.combineTab &&
                    typeof getFieldNames === "function"
                ) {
                    const label = getFieldNames(row);
                    if (label != null && label !== "" && label !== "-") {
                        return String(label);
                    }
                }
                const t = row?.type;
                const ts = t != null ? String(t) : "";
                if (ts.includes("admission") || ts.includes("readmission") || ts.includes("return")) {
                    return ts;
                }
                const tt = row?.transferType;
                return tt != null ? String(tt) : ts;
            }
            if (typeof getFieldNames === "function") {
                const label = getFieldNames(row);
                return label != null ? String(label) : "";
            }
            if (row?.type === "transfer" && row?.transferType) {
                return String(row.transferType);
            }
            return row?.type != null ? String(row.type) : "";
        },
        [isNotCombineTab, getFieldNames, filters?.question?.customTab?.type]
    );

    const getTypeColumnSortLabel = React.useCallback(
        (row) => {
            const raw = getTypeDisplayRaw(row);
            if (!raw || raw === "-") return raw || "";
            return capitalizeFirstLetter(raw);
        },
        [getTypeDisplayRaw]
    );

    const renderTypeCell = React.useCallback(
        (row, rowIndex) => {
            const displayText = getTypeColumnSortLabel(row);
            if (!displayText || displayText === "-") {
                return displayText || "-";
            }

            const content = highlightText(
                displayText,
                null,
                handleHighlightedCount,
                null,
                rowIndex
            );

            return content;
        },
        [getTypeColumnSortLabel, isMobile] // eslint-disable-line react-hooks/exhaustive-deps
    );

    // Function to sort the data
    const sortedData = patientDataOrderBy(chartData, order, orderBy, {
        getTypeSortLabel: getTypeColumnSortLabel,
    });

    const TableSortLabelHeader = (props) => {
        return (
            <TableSortLabel
                active={orderBy === props.name}
                direction={orderBy === props.name ? order : 'asc'}
                onClick={() => handleRequestSort(props.name)}
                sx={{
                    '& .MuiTableSortLabel-icon': {
                        opacity: 1,
                    },
                    '&:hover': {
                        color: 'inherit',
                    },
                }}
            >
                {props.label}
            </TableSortLabel>
        );
    }

    // Initialize column order
    React.useEffect(() => {
        if (questions && questions.length > 0) {
            setOriginalQuestions(questions);
            loadColumnOrder();
        } else {
            setColumnOrder([]);
            setIsLoadingColumnOrder(false);
        }
    }, [questions, loadColumnOrder]);

    React.useEffect(() => {
        if (questions && questions.length > 0 && ((pageType === DASHBOARD_FILTER_TYPE.COMMUNITY_TRANSFER && tableFilterType) || (filters?.question?.isAnotherDashboard && filters?.question?.dashboardType === DASHBOARD_FILTER_TYPE.COMMUNITY_TRANSFER))) {
            setColumnOrder([]);
            loadColumnOrder();
        }
    }, [tableFilterType]); // eslint-disable-line

    // Get fixed columns that cannot be reordered
    const getFixedColumns = () => {
        const baseColumns = [
            // Info column for mobile (always first)
            ...(isMobile ? [{ accessor: 'info', label: 'Edit', isFixed: false }] : []),
            ...(activeFacilities?.length > 1 ? [{ accessor: 'facilityName', label: 'facility', isFixed: !isMobile }] : []),
            { accessor: 'lastName', label: 'Last Name', isFixed: !isMobile },
            { accessor: 'firstName', label: 'First Name', isFixed: !isMobile },
            { accessor: 'DOB', label: 'DATE OF BIRTH', isFixed: !isMobile },
            {
                accessor: 'type',
                label: isNotCombineTab ? (relation || filters?.question?.cardTitle || filters?.cardTitle || "Type") : "Type",
                isFixed: !isMobile
            },
            { accessor: 'dateOfADT', label: 'Date Of Transfer', isFixed: !isMobile }
        ];

        // Only include edit column on desktop
        if (!isMobile) {
            baseColumns.unshift({ accessor: 'edit', label: 'Edit', isFixed: true });
        }

        return baseColumns;
    };

    // Get all columns in correct order
    const getAllColumns = () => {
        const fixedColumns = getFixedColumns();
        const reorderableColumns = getReorderableColumns();
        const hospitalDaysColumn = pageType === DASHBOARD_FILTER_TYPE.HOSPITAL && isNotCombineTab && !isOnlyHospitalDashboard && !filters?.question?.isAnotherDashboard
            ? [{ accessor: 'hospitalDays', label: '# of days in hospital', isFixed: false }]
            : [];

        // Add special columns for exhaustInsuranceData
        const exhaustInsuranceColumns = filters?.type === OVERALL_CARDS_TYPE.EXHUAST_INSURANCE_DATA
            ? [
                { accessor: 'exhaustionDate', label: 'Exhaustion Date', isFixed: false },
                { accessor: 'remaining', label: 'Remaining Days', isFixed: false },
                { accessor: 'secondaryInsurance', label: 'Secondary Insurance', isFixed: false }
            ]
            : [];

        return [...fixedColumns, ...reorderableColumns, ...hospitalDaysColumn, ...exhaustInsuranceColumns];
    };

    const isSearchableColumn = React.useCallback((accessor) => {
        if (!accessor) return false;
        return accessor !== "edit" && accessor !== "info";
    }, []);

    const getSearchCellText = React.useCallback(
        (row, accessor) => {
            if (!row || !accessor) return "";
            switch (accessor) {
                case "DOB":
                    return row?.DOB ? String(toDisplayTime(row.DOB, "MM/DD/YYYY")) : "";
                case "dateOfADT":
                    return row?.dateOfADT ? String(toDisplayTime(row.dateOfADT, "MM/DD/YYYY")) : "";
                case "type":
                    return String(getTypeColumnSortLabel(row) || "");
                case "secondaryInsurance": {
                    const label = row?.secondaryInsuranceLabel ?? row?.secondaryInsurance;
                    return label != null ? String(label) : "";
                }
                default: {
                    const v = row?.[accessor];
                    if (typeof v === "boolean") return v ? "Yes" : "No";
                    return v != null ? String(v) : "";
                }
            }
        },
        [getTypeColumnSortLabel]
    );

    const getActiveSearchColumnAccessors = React.useMemo(() => {
        const all = getAllColumns().map((c) => c.accessor).filter(isSearchableColumn);
        if (!selectedHighlightedColumns || selectedHighlightedColumns.length === 0) return all;
        if (selectedHighlightedColumns.includes("selectAll")) return all;
        return all.filter((a) => selectedHighlightedColumns.includes(a));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedHighlightedColumns, isMobile, columnOrder, activeFacilities?.length, filters?.type, pageType]);

    const cellMatchesSearch = React.useCallback(
        (row, accessor) => {
            if (!searchTerm || !accessor) return false;
            const raw = getSearchCellText(row, accessor);
            if (!raw) return false;

            const op = filterType?.operation;
            const isNumberOp = filterType?.type === "number" && !!op;

            if (isNumberOp) {
                const num = Number(raw);
                if (Number.isNaN(num)) return false;
                const target = Number(searchTerm);
                if (Number.isNaN(target)) return false;
                return op === "lessThan" ? num <= target : num >= target;
            }

            return String(raw).toLowerCase().includes(String(searchTerm).toLowerCase());
        },
        [searchTerm, filterType?.operation, filterType?.type, getSearchCellText]
    );

    // Column-first order: all matches in column 1 (top to bottom), then column 2, etc.
    const searchMatches = React.useMemo(() => {
        if (!searchTerm) return [];
        if (!sortedData || sortedData.length === 0) return [];
        if (!getActiveSearchColumnAccessors.length) return [];

        const matches = [];

        getActiveSearchColumnAccessors.forEach((accessor) => {
            sortedData.forEach((row, rowIndex) => {
                if (cellMatchesSearch(row, accessor)) {
                    matches.push({ rowIndex, accessor });
                }
            });
        });

        return matches;
    }, [
        searchTerm,
        sortedData,
        getActiveSearchColumnAccessors,
        cellMatchesSearch,
    ]);

    const columnSearchMatches = React.useMemo(() => {
        const byColumn = {};
        searchMatches.forEach((match) => {
            if (!byColumn[match.accessor]) {
                byColumn[match.accessor] = [];
            }
            byColumn[match.accessor].push(match.rowIndex);
        });
        return byColumn;
    }, [searchMatches]);

    const searchMatchKeySet = React.useMemo(() => {
        const set = new Set();
        searchMatches.forEach((m) => set.add(`${m.accessor}_${m.rowIndex}`));
        return set;
    }, [searchMatches]);

    const getSearchColumnLabel = React.useCallback(
        (accessor) => {
            const col = getAllColumns().find((c) => c.accessor === accessor);
            return col?.label || accessor || "";
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [columnOrder, isMobile, activeFacilities?.length, filters?.type, pageType]
    );

    const [activeSearchMatchIndex, setActiveSearchMatchIndex] = React.useState(0);
    const activeSearchMatchIndexRef = React.useRef(0);

    React.useEffect(() => {
        activeSearchMatchIndexRef.current = activeSearchMatchIndex;
    }, [activeSearchMatchIndex]);

    React.useEffect(() => {
        setActiveSearchMatchIndex(0);
    }, [searchTerm, filterType?.operation, filterType?.type, selectedHighlightedColumns, searchMatches.length]);

    const setTableContainerRef = React.useCallback(
        (node) => {
            localTableContainerRef.current = node;
            if (tableElementRef) {
                tableElementRef.current = node;
            }
        },
        [tableElementRef]
    );

    const getTableScrollContainer = React.useCallback(
        (cell) => {
            const fromRef = localTableContainerRef.current || tableElementRef?.current;
            if (fromRef) return fromRef;
            const byId = document.getElementById("chart-table-list-id");
            if (byId) return byId;
            return cell?.closest?.(".scrollable-container") || null;
        },
        [tableElementRef]
    );

    const getStickyColumnsWidth = React.useCallback((scrollContainer) => {
        if (!scrollContainer || isMobile) return 0;
        const headerRow = scrollContainer.querySelector("thead tr");
        if (!headerRow) return 0;
        const fixedCount = getAllColumns().filter((col) => col.isFixed).length;
        if (!fixedCount) return 0;
        const stickyCells = headerRow.querySelectorAll(`th:nth-child(-n+${fixedCount})`);
        return Array.from(stickyCells).reduce((acc, th) => acc + th.offsetWidth, 0);
    }, [isMobile]); // eslint-disable-line react-hooks/exhaustive-deps

    const scrollSearchMatchCellIntoView = React.useCallback(
        (match) => {
            if (!match) return false;
            const cellId = `${match.accessor}_cell_${match.rowIndex}`;
            const cell = document.getElementById(cellId);
            if (!cell) return false;

            const scrollContainer = getTableScrollContainer(cell);
            if (!scrollContainer) return false;

            const thead = scrollContainer.querySelector("thead");
            const headerHeight = thead?.offsetHeight ?? thead?.getBoundingClientRect?.().height ?? 52;
            const fixedColumnsWidth = getStickyColumnsWidth(scrollContainer);

            const containerRect = scrollContainer.getBoundingClientRect();
            const cellRect = cell.getBoundingClientRect();

            const nextTop =
                scrollContainer.scrollTop +
                (cellRect.top - containerRect.top) -
                headerHeight -
                12;
            const nextLeft =
                scrollContainer.scrollLeft +
                (cellRect.left - containerRect.left) -
                fixedColumnsWidth -
                16;

            scrollContainer.scrollTo({
                top: Math.max(nextTop, 0),
                left: Math.max(nextLeft, 0),
                behavior: "smooth",
            });

            return true;
        },
        [getTableScrollContainer, getStickyColumnsWidth]
    );

    const scrollToSearchMatchIndex = React.useCallback(
        (nextIndex) => {
            if (!searchMatches || searchMatches.length === 0) return;
            const normalized =
                ((nextIndex % searchMatches.length) + searchMatches.length) %
                searchMatches.length;
            const match = searchMatches[normalized];
            if (!match) return;

            setActiveSearchMatchIndex(normalized);

            const tryScroll = () => scrollSearchMatchCellIntoView(match);

            requestAnimationFrame(() => {
                if (!tryScroll()) {
                    window.setTimeout(tryScroll, 50);
                }
            });
            window.setTimeout(tryScroll, 150);
            window.setTimeout(tryScroll, 350);
        },
        [searchMatches, scrollSearchMatchCellIntoView]
    );

    React.useEffect(() => {
        if (searchTerm && searchMatches.length > 0) {
            scrollToSearchMatchIndex(0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchMatches.length, searchTerm]);

    React.useEffect(() => {
        if (!searchTerm) {
            resetChartTableSearchNav();
            handleHighlightedCount(0);
            return;
        }

        handleHighlightedCount(searchMatches.length);

        const activeMatch = searchMatches[activeSearchMatchIndex];
        const columnRows = activeMatch ? columnSearchMatches[activeMatch.accessor] || [] : [];
        const columnMatchIndex = activeMatch
            ? columnRows.indexOf(activeMatch.rowIndex)
            : 0;

        updateChartTableSearchNav({
            total: searchMatches.length,
            activeIndex: activeSearchMatchIndex,
            columnLabel: activeMatch ? getSearchColumnLabel(activeMatch.accessor) : "",
            columnMatchIndex: columnMatchIndex >= 0 ? columnMatchIndex : 0,
            columnMatchTotal: columnRows.length,
            goPrev: () =>
                scrollToSearchMatchIndex(activeSearchMatchIndexRef.current - 1),
            goNext: () =>
                scrollToSearchMatchIndex(activeSearchMatchIndexRef.current + 1),
        });
    }, [searchTerm, searchMatches.length, activeSearchMatchIndex, scrollToSearchMatchIndex, handleHighlightedCount, columnSearchMatches, getSearchColumnLabel]); // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => () => resetChartTableSearchNav(), []);

    const activeSearchMatch = searchMatches[activeSearchMatchIndex];
    const activeSearchColumn =
        searchTerm && activeSearchMatch ? activeSearchMatch.accessor : null;

    const tableHeaderOffset = isMobile ? 40 : 52;

    const tableBodyOverlaySx = {
        position: "absolute",
        left: 0,
        right: 0,
        bottom: 0,
        top: tableHeaderOffset,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        gap: 1.5,
        bgcolor: "background.paper",
        zIndex: 4,
        minHeight: 180,
    };

    const tableCellBorderSx = {
        border: "1px solid",
        borderColor: "divider",
    };

    const headerCellSx = {
        textTransform: "uppercase",
        fontFamily: isMobile ? '"manrope-reg"' : '"manrope-bold"',
        fontSize: isMobile ? "8px" : "11px",
        letterSpacing: "0.03em",
        lineHeight: isMobile ? 0.5 : 1.4,
        color: "text.secondary",
        py: isMobile ? 0.75 : 1.25,
        px: isMobile ? 1 : 1.5,
        backgroundColor: "#f8fafc",
        ...tableCellBorderSx,
        position: "sticky",
        top: 0,
        zIndex: 5,
        whiteSpace: "nowrap",
    };

    const bodyCellSx = {
        py: isMobile ? 0.5 : 0.875,
        px: isMobile ? 0.5 : 1.5,
        fontSize: isMobile ? "10px" : "13px",
        lineHeight: 1.35,
        ...tableCellBorderSx,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        verticalAlign: "middle",
    };

    const cellContentSx = {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        maxWidth: "100%",
        display: "block",
    };

    const dynamicColumnWidth = isMobile ? 100 : 180;

    const stickyHeaderBg = "#f8fafc";
    const stickyBodyBg = "#fff";

    return (
        <>
            <Box
                sx={{
                    width: "100%",
                    flex: 1,
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                }}
            >
                <TableContainer
                    component={Paper}
                    elevation={0}
                    className="scrollable-container print-table-scrollable"
                    ref={setTableContainerRef}
                    id="chart-table-list-id"
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                    onTouchCancel={handleTouchEnd}
                    sx={{
                        position: "relative",
                        overscrollBehavior: "none",
                        width: "100%",
                        maxWidth: "100%",
                        flex: 1,
                        minHeight: 0,
                        height: isMobile ? "calc(100vh - 90px)" : "100%",
                        maxHeight: isMobile ? "calc(100vh - 90px)" : "100%",
                        overflow: "auto",
                        WebkitOverflowScrolling: "touch",
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 2,
                        bgcolor: "background.paper",
                        ...(isMobile && {
                            overflowX: isScrollingVertically ? "hidden" : "auto",
                            overflowY: isScrollingHorizontally ? "hidden" : "auto",
                        }),
                    }}
                >
                    {isLoadingColumnOrder && (
                        <Box sx={tableBodyOverlaySx} aria-live="polite" aria-busy="true">
                            <CircularProgress size={36} />
                            <Typography variant="body2" color="text.secondary">
                                Loading table...
                            </Typography>
                        </Box>
                    )}
                    {!isLoadingColumnOrder && chartData.length === 0 && (
                        <Box sx={tableBodyOverlaySx}>
                            <Typography variant="subtitle1" color="text.secondary" fontWeight={500}>
                                No data found
                            </Typography>
                        </Box>
                    )}
                    <Table
                        key={`table-${columnOrder.map(c => c?.question?.accessor).join('-')}`}
                        className="print-table"
                        size={isMobile ? "small" : "medium"}
                        aria-label="Patient data table"
                        stickyHeader
                        sx={{
                            width: "max-content",
                            minWidth: "100%",
                            tableLayout: "auto",
                            borderCollapse: "collapse",
                            "& .MuiTableCell-root": tableCellBorderSx,
                        }}
                    >
                        <TableHead
                            sx={{
                                position: 'sticky',
                                top: 0,
                                zIndex: 5,
                                backgroundColor: stickyHeaderBg,
                                '& th': {
                                    backgroundColor: stickyHeaderBg,
                                    position: 'sticky',
                                    top: 0,
                                    zIndex: 5,
                                }
                            }}
                        >
                            <DndContext
                                sensors={sensors}
                                collisionDetection={closestCenter}
                                onDragEnd={handleDragEnd}
                            >
                                <TableRow sx={{ "& th": headerCellSx }}>
                                    {(() => {
                                        const allColumns = getAllColumns();
                                        const fixedColumns = allColumns.filter(col => col.isFixed);
                                        const reorderableColumns = allColumns.filter(col => !col.isFixed);

                                        return (
                                            <>
                                                {/* Render Fixed Columns */}
                                                {fixedColumns.map((column, index) => {
                                                    let leftPosition = null;
                                                    if (!isMobile) {
                                                        switch (column.accessor) {
                                                            case 'edit':
                                                                leftPosition = 0;
                                                                break;
                                                            case 'facilityName':
                                                                leftPosition = "100px";
                                                                break;
                                                            case 'lastName':
                                                                leftPosition = activeFacilities?.length > 1 ? "220px" : "100px";
                                                                break;
                                                            case 'firstName':
                                                                leftPosition = activeFacilities?.length > 1 ? "340px" : "220px";
                                                                break;
                                                            default:
                                                                leftPosition = null;
                                                        }
                                                    }

                                                    return (
                                                        <TableCell
                                                            key={column.accessor}
                                                            align="center"
                                                            style={{
                                                                ...(leftPosition !== null && !isMobile && {
                                                                    position: "sticky",
                                                                    left: leftPosition,
                                                                    top: 0,
                                                                    minWidth: column.accessor === 'edit' ? "100px" : "120px",
                                                                    zIndex: showAdd ? 11 : 15,
                                                                    backgroundColor: stickyHeaderBg,
                                                                }),
                                                                ...(column.accessor === 'type' && { minWidth: isMobile ? "120px" : "200px" }),
                                                                zIndex: leftPosition !== null && !isMobile ? (showAdd ? 11 : 15) : 10,
                                                                ...(activeSearchColumn === column.accessor && {
                                                                    backgroundColor: "rgba(254, 243, 199, 0.9)",
                                                                    boxShadow: "inset 0 -3px 0 #f59e0b",
                                                                }),
                                                            }}
                                                            sortDirection={orderBy === column.accessor ? order : false}
                                                        >
                                                            {column.accessor === 'edit' ? (
                                                                column.label
                                                            ) : (
                                                                <TableSortLabelHeader
                                                                    name={column.accessor === 'type' && isNotCombineTab ?
                                                                        ((filters?.type === "ninetyDaysData" || filters?.type === "sixtyDaysData") ? "countDay" : "type") :
                                                                        column.accessor
                                                                    }
                                                                    label={column.label}
                                                                    handleRequestSort={handleRequestSort}
                                                                />
                                                            )}
                                                        </TableCell>
                                                    );
                                                })}

                                                {/* Render Reorderable Columns */}
                                                {reorderableColumns.length > 0 && (
                                                    <SortableContext items={reorderableColumns.map(col => col.accessor)} strategy={horizontalListSortingStrategy}>
                                                        {reorderableColumns.map((column, dragIndex) => (
                                                            <SortableTableCell
                                                                key={column.accessor}
                                                                id={column.accessor}
                                                                align="center"
                                                                sortDirection={orderBy === column.accessor ? order : false}
                                                                sx={{
                                                                    minWidth: dynamicColumnWidth,
                                                                    maxWidth: dynamicColumnWidth + 40,
                                                                    ...(activeSearchColumn === column.accessor && {
                                                                        backgroundColor: "rgba(254, 243, 199, 0.9)",
                                                                        boxShadow: "inset 0 -3px 0 #f59e0b",
                                                                    }),
                                                                }}
                                                            >
                                                                <TableSortLabelHeader
                                                                    name={column.accessor}
                                                                    label={getTooltipText(
                                                                        column.label,
                                                                        column.accessor,
                                                                        false,
                                                                        null
                                                                    )}
                                                                    handleRequestSort={handleRequestSort}
                                                                />
                                                            </SortableTableCell>
                                                        ))}
                                                    </SortableContext>
                                                )}
                                            </>
                                        );
                                    })()}
                                </TableRow>
                            </DndContext>
                        </TableHead>
                        <TableBody sx={{
                            fontFamily: `"manrope-reg"`,
                            fontSize: isMobile ? "10px" : "13px",
                            '& .MuiTableCell-root': {
                                ...bodyCellSx,
                            },
                            '& .MuiTableRow-root:nth-of-type(even)': {
                                backgroundColor: isMobile ? 'transparent' : 'rgba(248, 250, 252, 0.6)',
                            },
                            '& .MuiTableRow-root:hover': {
                                backgroundColor: 'action.hover',
                            },
                            ...(isMobile && {
                                '& .MuiTypography-root': {
                                    fontFamily: `"manrope-reg"`,
                                    fontSize: "10px",
                                }
                            })
                        }}>
                            {chartData.length > 0 && (
                                <>
                                    {sortedData?.map((row, index) => {
                                        return (
                                            <TableRow
                                                key={index}
                                                data-chart-row-index={index}
                                                onClick={() => {
                                                    setSelectedResident(row);
                                                }}
                                                sx={{
                                                    cursor: "pointer",
                                                    transition: "background-color 0.15s ease",
                                                }}
                                                hover
                                            >
                                                {!isLoadingColumnOrder && getAllColumns().map((column, colIndex) => {
                                                    const isFixed = column.isFixed;
                                                    let leftPosition = null;

                                                    // Calculate sticky positions for fixed columns (only on desktop)
                                                    if (isFixed && !isMobile) {
                                                        switch (column.accessor) {
                                                            case 'edit':
                                                                leftPosition = 0;
                                                                break;
                                                            case 'facilityName':
                                                                leftPosition = "100px";
                                                                break;
                                                            case 'lastName':
                                                                leftPosition = activeFacilities?.length > 1 ? "220px" : "100px";
                                                                break;
                                                            case 'firstName':
                                                                leftPosition = activeFacilities?.length > 1 ? "340px" : "220px";
                                                                break;
                                                            default:
                                                                leftPosition = null;
                                                        }
                                                    }

                                                    const isActionColumn = column.accessor === "edit" || column.accessor === "info";
                                                    const cellValue = (() => {
                                                        switch (column.accessor) {
                                                            case 'info':
                                                                return (
                                                                    <Stack direction={"row"} spacing={1} justifyContent="center">
                                                                        <Stack style={{ display: "flex", alignItems: "center", justifyContent: "center" }} item sx={{
                                                                            "div": {
                                                                                marginTop: "6px",
                                                                                width: "18px",
                                                                                height: "20px",
                                                                                display: "flex",
                                                                                marginLeft: "-6px",
                                                                                marginRight: "-5px",
                                                                                alignItems: "center",
                                                                                justifyContent: "center",
                                                                                "svg": {
                                                                                    top: "8px",
                                                                                    left: "7px",
                                                                                    width: "26px",
                                                                                    height: "26px",
                                                                                }
                                                                            },
                                                                        }}>
                                                                            <HtmlTooltip
                                                                                content={<ActivityLogTooltip logs={row.logs ?? []} />}
                                                                                placement="right"
                                                                                sx={{
                                                                                    "& .MuiTooltip-tooltip": {
                                                                                        width: "360px",
                                                                                        maxWidth: "360px",
                                                                                        padding: 0,
                                                                                    },
                                                                                }}
                                                                            >
                                                                                <IconButton size="small">
                                                                                    <InfoIcon />
                                                                                </IconButton>
                                                                            </HtmlTooltip>
                                                                        </Stack>
                                                                        <Stack item style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                                            {row &&
                                                                                row?.highlighter &&
                                                                                row?.highlighter.length > 0 &&
                                                                                row?.highlighter.filter((ele) => !_.includes(["suffix", "middleInitial"], ele?.fieldName))?.map((ele, jIndex) => (
                                                                                    <Box
                                                                                        key={jIndex}
                                                                                        sx={{ mt: "5px" }}
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            handleScrollTo(
                                                                                                ele?.fieldName + "_cell_" + index,
                                                                                                ele?.color,
                                                                                                ele?.fieldName
                                                                                            );
                                                                                        }}
                                                                                    >
                                                                                        <Tooltip
                                                                                            disableHoverListener={!ele?.description}
                                                                                            title={ele?.description}
                                                                                            key={jIndex + "tooltip"}
                                                                                            arrow={true}
                                                                                            placement="right"
                                                                                        >
                                                                                            <div>
                                                                                                <Box
                                                                                                    sx={{
                                                                                                        width: 10,
                                                                                                        height: 10,
                                                                                                        backgroundColor: ele?.color,
                                                                                                        borderRadius: "50%",
                                                                                                        marginBottom: 0.5,
                                                                                                    }}
                                                                                                />
                                                                                            </div>
                                                                                        </Tooltip>
                                                                                    </Box>
                                                                                ))}
                                                                        </Stack>
                                                                    </Stack>
                                                                );
                                                            case 'edit':
                                                                return (
                                                                    <Stack direction={"row"} spacing={1}>
                                                                        {isWriteAccess && (
                                                                            <>
                                                                                <Stack style={{ display: "flex", alignItems: "center", justifyContent: "center" }} item sx={{
                                                                                    "div": {
                                                                                        marginTop: "6px",
                                                                                        width: "18px",
                                                                                        height: "20px",
                                                                                        display: "flex",
                                                                                        marginLeft: "-6px",
                                                                                        marginRight: "-5px",
                                                                                        alignItems: "center",
                                                                                        justifyContent: "center",
                                                                                        "svg": {
                                                                                            top: "8px",
                                                                                            left: "7px",
                                                                                            width: "26px",
                                                                                            height: "26px",
                                                                                        }
                                                                                    },
                                                                                }}>
                                                                                    <HtmlTooltip
                                                                                        content={<ActivityLogTooltip logs={row.logs ?? []} />}
                                                                                        placement="right"
                                                                                        sx={{
                                                                                            "& .MuiTooltip-tooltip": {
                                                                                                width: "360px",
                                                                                                maxWidth: "360px",
                                                                                                padding: 0,
                                                                                            },
                                                                                        }}
                                                                                    >
                                                                                        <IconButton size="small">
                                                                                            <InfoIcon />
                                                                                        </IconButton>
                                                                                    </HtmlTooltip>
                                                                                </Stack>
                                                                                <Stack item style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                                                    <IconButton onClick={(e) => {
                                                                                        e.preventDefault();
                                                                                        e.stopPropagation();
                                                                                        setEditResident(row);
                                                                                        toggleAdd();
                                                                                    }}>
                                                                                        <EditSVG />
                                                                                    </IconButton>
                                                                                </Stack>
                                                                            </>
                                                                        )}
                                                                        <Stack item style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                                            {row &&
                                                                                row?.highlighter &&
                                                                                row?.highlighter.length > 0 &&
                                                                                row?.highlighter.filter((ele) => !_.includes(["suffix", "middleInitial"], ele?.fieldName))?.map((ele, jIndex) => (
                                                                                    <Box
                                                                                        key={jIndex}
                                                                                        sx={{ mt: "5px" }}
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            handleScrollTo(
                                                                                                ele?.fieldName + "_cell_" + index,
                                                                                                ele?.color,
                                                                                                ele?.fieldName
                                                                                            );
                                                                                        }}
                                                                                    >
                                                                                        <Tooltip
                                                                                            disableHoverListener={!ele?.description}
                                                                                            title={ele?.description}
                                                                                            key={jIndex + "tooltip"}
                                                                                            arrow={true}
                                                                                            placement="right"
                                                                                        >
                                                                                            <div>
                                                                                                <Box
                                                                                                    sx={{
                                                                                                        width: 10,
                                                                                                        height: 10,
                                                                                                        backgroundColor: ele?.color,
                                                                                                        borderRadius: "50%",
                                                                                                        marginBottom: 0.5,
                                                                                                    }}
                                                                                                />
                                                                                            </div>
                                                                                        </Tooltip>
                                                                                    </Box>
                                                                                ))}
                                                                        </Stack>
                                                                    </Stack>
                                                                );
                                                            case 'facilityName':
                                                                return highlightText(`${row?.facilityName}`, "facilityName", handleHighlightedCount, row?.facilityName, index);
                                                            case 'lastName':
                                                                return highlightText(`${row.lastName}`, "lastName", handleHighlightedCount, null, index);
                                                            case 'firstName':
                                                                return highlightText(`${row.firstName}`, "firstName", handleHighlightedCount, null, index);
                                                            case 'DOB':
                                                                return highlightText(toDisplayTime(row.DOB, "MM/DD/YYYY"), "DOB", handleHighlightedCount, null, index);
                                                            case 'type':
                                                                return renderTypeCell(row, index);
                                                            case 'dateOfADT':
                                                                return highlightText(toDisplayTime(row.dateOfADT, "MM/DD/YYYY"), "dateOfADT", handleHighlightedCount, null, index);
                                                            case 'hospitalDays':
                                                                return highlightText(`${row?.hospitalDays ?? ''}`, "hospitalDays", handleHighlightedCount, null, index);
                                                            case 'exhaustionDate':
                                                                // Display exhaustionDate if available, otherwise calculate from dateOfADT + medicareDaysRemaining
                                                                const exhaustionDate = getMedicareExhaustionDate(row);
                                                                if (exhaustionDate) {
                                                                    return highlightText(toDisplayTime(exhaustionDate.toDate(), "MM/DD/YYYY"), "exhaustionDate", handleHighlightedCount, null, index);
                                                                }
                                                                return "-";
                                                            case 'remaining':
                                                                // Display medicareDaysRemaining
                                                                const remaining = row.medicareDaysRemaining;
                                                                if (remaining != null && remaining !== undefined) {
                                                                    return highlightText(`${remaining}`, "remaining", handleHighlightedCount, null, index);
                                                                }
                                                                return "-";
                                                            case 'secondaryInsurance':
                                                                // Display secondaryInsurance label if available
                                                                if (row.secondaryInsurance) {
                                                                    const secondaryInsuranceLabel = row.secondaryInsurance?.label || row.secondaryInsurance?.name || row.secondaryInsurance;
                                                                    return highlightText(secondaryInsuranceLabel, "secondaryInsurance", handleHighlightedCount, null, index);
                                                                }
                                                                return "-";
                                                            default:
                                                                // Handle reorderable columns (questions)
                                                                return getQuestionAnswer(column.question, row, index);
                                                        }
                                                    })();

                                                    const hasSearchMatchInCell =
                                                        Boolean(searchTerm) &&
                                                        searchMatchKeySet.has(
                                                            `${column.accessor}_${index}`
                                                        );
                                                    const isActiveSearchCell =
                                                        Boolean(searchTerm) &&
                                                        activeSearchMatch?.rowIndex === index &&
                                                        activeSearchMatch?.accessor === column.accessor;

                                                    return (
                                                        <TableCell
                                                            key={`${column.accessor}-${index}-${colIndex}`}
                                                            align={!isMobile && (column.accessor === 'edit' || column.accessor === 'lastName' || column.accessor === 'firstName') ? "left" : "center"}
                                                            style={{
                                                                ...(leftPosition !== null && !isMobile && {
                                                                    position: "sticky",
                                                                    left: leftPosition,
                                                                    minWidth: column.accessor === 'edit' ? "100px" : "120px",
                                                                    zIndex: isActiveSearchCell ? 6 : showAdd ? 5 : 1,
                                                                    backgroundColor: isActiveSearchCell
                                                                        ? "rgba(254, 243, 199, 0.95)"
                                                                        : stickyBodyBg,
                                                                }),
                                                                borderBottom: getHighLightColor(column.accessor, row)
                                                                    ? `2px solid ${getHighLightColor(column.accessor, row)}`
                                                                    : undefined,
                                                            }}
                                                            component={!isMobile && (column.accessor === 'edit' || column.accessor === 'lastName' || column.accessor === 'firstName') ? "th" : "td"}
                                                            scope={!isMobile && (column.accessor === 'edit' || column.accessor === 'lastName' || column.accessor === 'firstName') ? "row" : undefined}
                                                            id={`${column.accessor}_cell_${index}`}
                                                            data-search-active={isActiveSearchCell ? "true" : undefined}
                                                            sx={{
                                                                ...bodyCellSx,
                                                                ...(hasSearchMatchInCell &&
                                                                    !isActiveSearchCell && {
                                                                    backgroundColor:
                                                                        "rgba(254, 243, 199, 0.45) !important",
                                                                }),
                                                                ...(isActiveSearchCell && {
                                                                    outline: "2px solid #f59e0b",
                                                                    outlineOffset: -2,
                                                                    backgroundColor: "rgba(254, 243, 199, 0.9) !important",
                                                                    boxShadow: "inset 0 0 0 1px rgba(245, 158, 11, 0.5)",
                                                                    zIndex: 3,
                                                                }),
                                                                ...(isActionColumn && {
                                                                    whiteSpace: "normal",
                                                                    overflow: "visible",
                                                                    textOverflow: "clip",
                                                                }),
                                                                ...(!column.isFixed && !isActionColumn && {
                                                                    minWidth: dynamicColumnWidth,
                                                                    maxWidth: dynamicColumnWidth + 40,
                                                                }),
                                                                ...(["lastName", "firstName", "facilityName"].includes(column.accessor) && {
                                                                    maxWidth: isMobile ? 90 : 140,
                                                                }),
                                                                ...(column.accessor === "type" && {
                                                                    whiteSpace: "normal",
                                                                    overflow: "visible",
                                                                    textOverflow: "clip",
                                                                }),
                                                            }}
                                                        >
                                                            {column.accessor === "type" ? (
                                                                cellValue
                                                            ) : isActionColumn ? (
                                                                cellValue
                                                            ) : (
                                                                <Box component="span" sx={cellContentSx}>
                                                                    {cellValue}
                                                                </Box>
                                                            )}
                                                        </TableCell>
                                                    );
                                                })}
                                            </TableRow>
                                        );
                                    })}
                                    {pageType && pageType === DASHBOARD_FILTER_TYPE.HOSPITAL && isNotCombineTab && !filters?.question?.isAnotherDashboard && (
                                        <>
                                            <TableRow sx={{ bgcolor: "#f8fafc" }}>
                                                <TableCell
                                                    colSpan={
                                                        pageType === DASHBOARD_FILTER_TYPE.HOSPITAL ||
                                                            pageType === DASHBOARD_FILTER_TYPE.DECEASED ||
                                                            pageType === DASHBOARD_FILTER_TYPE.OVERALL ||
                                                            pageType === DASHBOARD_FILTER_TYPE.COMMUNITY_TRANSFER
                                                            ? pageType ===
                                                                DASHBOARD_FILTER_TYPE.COMMUNITY_TRANSFER
                                                                ? 18
                                                                : (!isOnlyHospitalDashboard ? 5 : 3) +
                                                                questions.filter(question => {
                                                                    const accessor = question?.question?.accessor;
                                                                    if (isAdt && accessor === 'dateOfLatestAdmission') return false;
                                                                    return true;
                                                                })?.length
                                                            : 15
                                                    }
                                                    sx={{ borderTop: "2px solid", borderColor: "divider" }}
                                                >
                                                    &nbsp;
                                                </TableCell>
                                                <TableCell
                                                    align="left"
                                                    sx={{
                                                        borderTop: "2px solid",
                                                        borderColor: "divider",
                                                        py: 1.5,
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    <Typography variant="subtitle2" component="span" color="text.secondary">
                                                        Total:{" "}
                                                    </Typography>
                                                    <Typography variant="body2" component="span" fontWeight={600}>
                                                        {_.sumBy(chartData, "hospitalDays")}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                            <TableRow sx={{ bgcolor: "#f8fafc" }}>
                                                <TableCell
                                                    colSpan={
                                                        pageType === DASHBOARD_FILTER_TYPE.HOSPITAL ||
                                                            pageType === DASHBOARD_FILTER_TYPE.DECEASED ||
                                                            pageType === DASHBOARD_FILTER_TYPE.OVERALL ||
                                                            pageType === DASHBOARD_FILTER_TYPE.COMMUNITY_TRANSFER
                                                            ? pageType ===
                                                                DASHBOARD_FILTER_TYPE.COMMUNITY_TRANSFER
                                                                ? 18
                                                                : (!isOnlyHospitalDashboard ? 5 : 3) +
                                                                questions.filter(question => {
                                                                    const accessor = question?.question?.accessor;
                                                                    if (isAdt && accessor === 'dateOfLatestAdmission') return false;
                                                                    return true;
                                                                })?.length
                                                            : 15
                                                    }
                                                >
                                                    &nbsp;
                                                </TableCell>
                                                <TableCell align="left" sx={{ py: 1.5, fontWeight: 600 }}>
                                                    <Typography variant="subtitle2" component="span" color="text.secondary">
                                                        Avg.:{" "}
                                                    </Typography>
                                                    <Typography variant="body2" component="span" fontWeight={600}>
                                                        {_.round(
                                                            _.meanBy(chartData, (p) => p?.hospitalDays) || 0,
                                                            2
                                                        ).toFixed(2)}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        </>
                                    )}
                                </>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
            {showAdd ? (
                <AddPatient
                    initialDate={initialDate}
                    close={toggleAdd}
                    patientAdded={patientAdded}
                    selectedResident={editResident}
                    patientUpdated={patientAdded}
                    patientId={editResident?._id}
                    step={2}
                    type="quickEdit"
                    isMultipleFacility={permission?.isMultipleFacility}
                />
            ) : undefined}
            {selectedResident ? (
                <ResidentDetails
                    details={selectedResident}
                    close={() => setSelectedResident(undefined)}
                    tableColumns={getTableColumnsForDetails()}
                />
            ) : undefined}
        </>
    );
}
